<template>
    <div>
        <progress-header />

        <div class="container mt-4">
            <div class="row justify-content-center pt-4" v-if="isLoading">
                <div class="spinner-border" role="status">
                    <span class="sr-only">{{ $t('loading') }}...</span>
                </div>
            </div>

            <div class="alert alert-danger" v-if="errors.length > 0">
                <div v-for="(error, idx) in errors" :key="idx">
                    {{ error }}
                </div>
            </div>

            <div v-if="!isLoading && criticalErrors.length > 0">
                <router-link :to="{ name: 'berbus.index' }">
                    {{ $t('berbus.pages.address.go_back') }}
                </router-link>
            </div>

            <form v-if="!isLoading && criticalErrors.length === 0" @submit.prevent="handleSubmit">
                <div class="card booking-form-card shadow-sm">
                    <div class="card-header">
                        {{ $t('berbus.pages.address.header') }}
                    </div>

                    <div class="px-4 py-3" v-if="isStartVendor">
                        <div class="text-lg text-blue-600 font-weight-bold mb-3">
                            {{ $t('berbus.pages.address.address_from') }}
                        </div>

                        <address-address :address="vendorAddresses.start" @updated="onStartAddressUpdated" />
                    </div>

                    <div class="px-4 py-3" v-if="isEndVendor">
                        <div class="text-lg text-blue-600 font-weight-bold mb-3">
                            {{ $t('berbus.pages.address.address_to') }}
                        </div>

                        <address-address :address="vendorAddresses.end" @updated="onEndAddressUpdated" />
                    </div>
                </div>

                <div class="my-4">
                    <v-submit-button :is-sending="isSending" class="btn btn-primary">
                        {{ $t('berbus.pages.address.submit') }}
                    </v-submit-button>
                </div>
            </form>
            <div class="row">
                <div class="col-12">
                    <stop-info />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import {
        BERBUS_PLACE_VENDOR_GMAPS,
        BERBUS_PLACE_VENDORS,
        PASSENGER_TYPE_ADULT,
        PASSENGER_TYPE_CHILD_AGE_3,
    } from '@/constants';
    import TravelRepository from '@repositories/berbus/TravelRepository';
    import GoogleGeoAddress from './GoogleGeoAddress';
    import GoogleLatLngBoundsPreset from './GoogleLatLngBoundsPreset';
    import AddressAddress from './components/Address/AddressAddress';
    import ProgressHeader from './components/ProgressHeader';
    import VSubmitButton from '@components/VSubmitButton';
    import {trackGA} from '@utilities/tracking';
    import StopInfo from '@/pages/BerbusPage/components/StopInfo.vue';
    import * as Sentry from '@sentry/browser';
    import backofficeRepository from '@repositories/BackofficeRepository';

    export default {
        components: {
            StopInfo,
            AddressAddress,
            ProgressHeader,
            VSubmitButton,
        },

        data() {
            return {
                isLoading: true,
                isSending: false,

                isStartVendor: false,
                isEndVendor: false,

                isStartAddressComplete: true,
                isEndAddressComplete: true,

                startZone: null,
                endZone: null,

                criticalErrors: [],
                validationErrors: [],
            };
        },

        computed: {
            ...mapGetters({
                search: 'berbus/search',
                vendorAddresses: 'berbus/vendorAddresses',
                isVendorStartAddressComplete: 'berbus/isVendorStartAddressComplete',
                isVendorEndAddressComplete: 'berbus/isVendorEndAddressComplete',
            }),

            errors() {
                return this.criticalErrors.concat(this.validationErrors);
            },
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 2,
                checkout_option: window.location.pathname,
            });

            if (!BERBUS_PLACE_VENDORS.includes(this.$route.query.start_vendor) || !BERBUS_PLACE_VENDORS.includes(this.$route.query.end_vendor)) {
                this.criticalErrors.push(this.$t('berbus.pages.address.errors.invalid_search_param'));
                this.isLoading = false;

                return;
            }

            if (!this.$route.query.passengers_adult) {
                this.criticalErrors.push(this.$t('berbus.pages.address.errors.invalid_passengers_adult'));
                this.isLoading = false;

                return;
            }

            this.isStartVendor = this.$route.query.start_vendor === BERBUS_PLACE_VENDOR_GMAPS;
            this.isEndVendor = this.$route.query.end_vendor === BERBUS_PLACE_VENDOR_GMAPS;

            let startGeoLookup = Promise.resolve(this.$route.query.start_address);
            let endGeoLookup = Promise.resolve(this.$route.query.end_address);

            const sentryLogExtras = {
                query: this.$route.query,
                isStartVendor: this.isStartVendor,
                isEndVendor: this.isEndVendor,
            };

            //reset zip - if address is not precise only when other fields are filled or building number is not filled (most likely zip is incorrect)
            if (this.isStartVendor) {
                startGeoLookup = this.googleFindAddressGeo(this.$route.query.start_address).then((addresses) => {
                    const address = new GoogleGeoAddress(addresses);
                    const addressObject = address.toObject();
                    sentryLogExtras.startAddress = addressObject;

                    //handle less accurate addresses and fill building number manually if empty
                    if (address.$address.geometry.location_type === 'GEOMETRIC_CENTER' && !addressObject.building_number) {
                        const buildingNumber = this.$route.query.start_address.split(',')[0].match(/\s(\d+)$/);
                        if (buildingNumber && buildingNumber.length > 1) {
                            addressObject.building_number = buildingNumber[1];
                        }
                    }

                    if(address.$address.geometry.location_type !== 'ROOFTOP' && address.$address.geometry.location_type !== 'GEOMETRIC_CENTER'){
                        if((addressObject.building_number && addressObject.street && addressObject.city) || !addressObject.building_number){
                            //reset zip
                            addressObject.zip = null;
                        }
                    }
                    this.$store.dispatch('berbus/setVendorStartAddress', addressObject);

                    if (this.isVendorStartAddressComplete) {
                        this.startAddressZoneFound = null;
                        return this.findZone(address.lat(), address.lng()).then((zone) => {
                            sentryLogExtras.startZone = zone;
                            if(!zone){
                                this.startAddressZoneFound = false;
                                //reset zip
                                if((addressObject.building_number && addressObject.street && addressObject.city) || !addressObject.building_number){
                                    addressObject.zip = null;
                                }
                                this.$store.dispatch('berbus/setVendorStartAddress', addressObject);
                            }
                            else{
                                this.startAddressZoneFound = true;
                            }
                            return zone;
                        });
                    }

                    return Promise.resolve(null);
                });
            }
            else {
                startGeoLookup = backofficeRepository.searchMapPlaceDetails(this.$route.query.start_address_id).then( (ret_address) => {
                    this.$store.dispatch('berbus/setVendorStartAddress', {
                        street: ret_address.name,
                        building_number: null,
                        zip: ret_address.postalCode,
                        city: ret_address.cityCode,
                        geo: {
                            lat: ret_address.locationLatitude,
                            lng: ret_address.locationLongitude,
                        },
                    });
                    return ret_address.name;
                });
            }

            if (this.isEndVendor) {
                endGeoLookup = this.googleFindAddressGeo(this.$route.query.end_address).then((addresses) => {
                    const address = new GoogleGeoAddress(addresses);
                    const addressObject = address.toObject();
                    sentryLogExtras.endAddress = addressObject;

                    //handle less accurate addresses and fill building number manually if empty
                    if (address.$address.geometry.location_type === 'GEOMETRIC_CENTER' && !addressObject.building_number) {
                        const buildingNumber = this.$route.query.end_address.split(',')[0].match(/\s(\d+)$/);
                        if (buildingNumber && buildingNumber.length > 1) {
                            addressObject.building_number = buildingNumber[1];
                        }
                    }

                    if(address.$address.geometry.location_type !== 'ROOFTOP' && address.$address.geometry.location_type !== 'GEOMETRIC_CENTER'){
                        if((addressObject.building_number && addressObject.street && addressObject.city) || !addressObject.building_number){
                            //reset zip
                            addressObject.zip = null;
                        }
                    }
                    this.$store.dispatch('berbus/setVendorEndAddress', addressObject);

                    if (this.isVendorEndAddressComplete) {
                        this.endAddressZoneFound = null;
                        return this.findZone(address.lat(), address.lng()).then((zone) => {
                            sentryLogExtras.endZone = zone;
                            if(!zone){
                                this.endAddressZoneFound = false;
                                if((addressObject.building_number && addressObject.street && addressObject.city) || !addressObject.building_number){
                                    //reset zip
                                    addressObject.zip = null;
                                }
                                this.$store.dispatch('berbus/setVendorEndAddress', addressObject);
                            }
                            else{
                                this.endAddressZoneFound = true;
                            }
                            return zone;
                        });
                    }

                    return Promise.resolve(null);
                });
            }
            else {
                endGeoLookup = backofficeRepository.searchMapPlaceDetails(this.$route.query.end_address_id).then( (ret_address) => {
                    this.$store.dispatch('berbus/setVendorEndAddress', {
                        street: ret_address.name,
                        building_number: null,
                        zip: ret_address.postalCode,
                        city: ret_address.cityCode,
                        geo: {
                            lat: ret_address.locationLatitude,
                            lng: ret_address.locationLongitude,
                        },
                    });
                    return ret_address.name;
                });
            }

            return Promise.all([startGeoLookup, endGeoLookup]).then((addresses) => {
                this.isLoading = false;

                if (this.isStartVendor && !this.startAddressZoneFound && !addresses[0]) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.start_zone_not_found'));
                }

                if (this.isEndVendor && !this.endAddressZoneFound && !addresses[1]) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.end_zone_not_found'));
                }

                if (this.isStartVendor && !addresses[0]) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.incomplete_start_address'));
                }

                if (this.isEndVendor && !addresses[1]) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.incomplete_end_address'));
                }

                if (this.validationErrors.length > 0) {
                    Sentry.withScope(scope => {
                        scope.setExtras(sentryLogExtras);
                        Sentry.captureMessage('BERBUS search address validationErrors', 'log');
                    });
                    return;
                }

                return this.storeAndRedirect(addresses[0], addresses[1]);
            });
        },

        methods: {
            onStartAddressUpdated(address) {
                this.$store.commit('berbus/setVendorStartAddress', address);
            },

            onEndAddressUpdated(address) {
                this.$store.commit('berbus/setVendorEndAddress', address);
            },

            handleSubmit() {
                this.validationErrors = [];

                if (this.isStartVendor && !this.isVendorStartAddressComplete) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.incomplete_start_address'));
                }

                if (this.isEndVendor && !this.isVendorEndAddressComplete) {
                    this.validationErrors.push(this.$t('berbus.pages.address.errors.incomplete_end_address'));
                }

                if (this.validationErrors.length > 0) {
                    return;
                }

                this.isSending = true;

                let startGeoLookup = Promise.resolve(this.$route.query.start_address);
                let endGeoLookup = Promise.resolve(this.$route.query.end_address);

                if (this.isStartVendor) {
                    const startAddress = `${this.vendorAddresses.start.street} ${this.vendorAddresses.start.building_number}, ${this.vendorAddresses.start.zip} ${this.vendorAddresses.start.city}`;

                    startGeoLookup = this.findGeoAndZone(startAddress, this.vendorAddresses.start.zip, 'berbus/setVendorStartAddress', this.vendorAddresses.start);
                }

                if (this.isEndVendor) {
                    const endAddress = `${this.vendorAddresses.end.street} ${this.vendorAddresses.end.building_number}, ${this.vendorAddresses.end.zip} ${this.vendorAddresses.end.city}`;

                    endGeoLookup = this.findGeoAndZone(endAddress, this.vendorAddresses.end.zip, 'berbus/setVendorEndAddress', this.vendorAddresses.end);
                }

                return Promise.all([startGeoLookup, endGeoLookup]).then((addresses) => {
                    if (this.isStartVendor && !addresses[0]) {
                        this.validationErrors.push(this.$t('berbus.pages.address.errors.start_zone_not_found'));
                        return;
                    }

                    if (this.isEndVendor && !addresses[1]) {
                        this.validationErrors.push(this.$t('berbus.pages.address.errors.end_zone_not_found'));
                        return;
                    }

                    return this.storeAndRedirect(addresses[0], addresses[1]);
                }).finally(() => {
                    this.isSending = false;
                });
            },

            storeAndRedirect(startAddress, endAddress) {
                this.$store.dispatch('berbus/setSearch', {
                    start_place: startAddress,
                    start_place_id: this.$route.query.start_address_id,
                    start_vendor: this.$route.query.start_vendor,
                    end_place: endAddress,
                    end_place_id: this.$route.query.end_address_id,
                    end_vendor: this.$route.query.end_vendor,
                    departure_date: this.$route.query.departure_date,
                    departure_time: this.$route.query.departure_time,
                    passengers: [
                        {
                            type: PASSENGER_TYPE_ADULT,
                            quantity: parseInt(this.$route.query.passengers_adult),
                        },
                        {
                            type: PASSENGER_TYPE_CHILD_AGE_3,
                            quantity: this.$route.query.passengers_child_age_3 ? parseInt(this.$route.query.passengers_child_age_3) : 0,
                        },
                    ],
                });

                return this.$router.push({ name: 'berbus.vehicles' });
            },

            findGeoAndZone(address, zipcode, storeKey = null, currentAddressObj = null) {
                return this.googleFindAddressGeo(address, zipcode).then((addresses) => {
                    if(!addresses){
                        return null;
                    }
                    const address = new GoogleGeoAddress(addresses);
                    //store recalculated geo positions only
                    if(storeKey !== null && currentAddressObj !== null){
                        currentAddressObj.geo.lat = address.lat();
                        currentAddressObj.geo.lng = address.lng();
                        this.$store.dispatch(storeKey, currentAddressObj);
                    }

                    return this.findZone(address.lat(), address.lng());
                });
            },

            findZone(lat, lng) {
                //@FIXME translate to BO API
                return TravelRepository.findZone(lat, lng).then(({ data }) => {
                    return data.data.name;
                });
            },

            googleFindAddressGeo(address, zipcode = null) {
                const geocoder = new window.google.maps.Geocoder();
                const componentRestrictions = zipcode?{postalCode: zipcode}:null;

                return new Promise((resolve, reject) => {
                    geocoder.geocode({
                        address,
                        componentRestrictions,
                        bounds: GoogleLatLngBoundsPreset,
                        language: window.APP.locale,
                    }, (results, status) => {
                        if (status !== 'OK' && status !== 'ZERO_RESULTS') {
                            this.criticalErrors.push(this.$t('berbus.pages.address.errors.geo_response_error'));
                            reject();
                        }
                        resolve(results);
                    });
                });
            },
        },
    };
</script>
